import React, { useEffect, useState } from "react"

// Utilities
import { extractImageNameAndColor, getUrlParam, reverseFormatExtractedColor } from "../../utilities/formatString"
import { formatProductTitle, handleProductIconByType } from "../../utilities/formatProductInfo"
import { checkOutOfStock } from "../../utilities/checkOutOfStock"
import lazyLoad from "../../utilities/handleLazyLoad"

// Components
import { Link } from "gatsby"

const ProductBoxImage = lazyLoad(() => import("./ProductBoxImage"))
const OutOfStock = lazyLoad(() => import("../ProductInfo/ProductInfoComponents/OutOfStock"))

/**
 * @param handleOpenQuickBuyModal - open popup
 * @param handleProduct - set selected product
 * @param type - product type
 * @param shopifyProduct - product object from Shopify
 * @param handleShopify - set selected shopify item
 * @param product - product object from local json
 * @param imgOrientation - img style: vertical, vertical-top or ''
 * @param color - default color
 * @param handleSelectedProductColor - update selected color
 * @param aboveTheFold - is section above the fold
 * **/
const ProductBox = ({
  handleOpenQuickBuyModal,
  handleProduct,
  type,
  shopifyProduct,
  handleShopify,
  product,
  imgOrientation,
  color,
  handleSelectedProductColor,
  aboveTheFold,
}) => {
  // Check if it's product or apparel/merchandise
  product = Object.keys(product).length > 1 ? product : Object.values(product)[0]

  /*=========================
      URL PARAMETER
      - if the product has multiple colors, add ?color parameter to the link
   =========================*/
  const urlParam = getUrlParam(shopifyProduct, color)

  /*=========================
    PRODUCT DATA
   =========================*/
  const title = formatProductTitle(shopifyProduct.title, shopifyProduct)
  const [notice, setNotice] = useState("")

  // Check if product is out of stock
  const isOutOfStock = checkOutOfStock(shopifyProduct)

  // Update image src and set notice
  useEffect(() => {
    // Update image src
    handleImageSrc()

    // Set product notice
    if (shopifyProduct.variants[0].compareAtPrice) {
      setNotice("sale")
    }

    if (
      shopifyProduct.handle === "my-dinner-plan" ||
      shopifyProduct.handle === "racerback-tank" ||
      shopifyProduct.handle === "rockstar-crop-top" ||
      shopifyProduct.handle === "stop-drop-and-betty-rock-crop-top" ||
      shopifyProduct.handle === "rock-your-life-crop-top" ||
      shopifyProduct.handle === "all-or-something-crop-top" ||
      shopifyProduct.handle === "fleece-lined-hoodie" ||
      shopifyProduct.handle === "all-or-something-racerback-tank" ||
      shopifyProduct.handle === "rock-your-life-racerback-tank" ||
      shopifyProduct.handle === "rock-your-life-muscle-tank" ||
      shopifyProduct.handle === "stop-drop-and-betty-rock-muscle-tank" ||
      shopifyProduct.handle === "all-or-something-muscle-tank" ||
      shopifyProduct.handle === "rock-your-life-slouchy-t" ||
      shopifyProduct.handle === "all-or-something-t-unisex"
    ) {
      setNotice("new")
    }
  }, [])

  /*=========================
    PRODUCT IMAGE
   =========================*/
  const [imageSrc, setImageSrc] = useState("")

  // Update image src according to selected color
  const handleImageSrc = () => {
    if (color && type !== "merchandise") {
      let reverseFormattedColor = reverseFormatExtractedColor(color)

      shopifyProduct.media.forEach(media => {
        let imageNameAndColor = extractImageNameAndColor(media.image.originalSrc)

        if (reverseFormattedColor === imageNameAndColor.color) {
          setImageSrc(`${imageNameAndColor.name}__${imageNameAndColor.color}`)
        }
      })
    } else {
      setImageSrc(product.img[0])
    }
  }

  /*=========================
    PRODUCT ICON
   =========================*/
  const icon = handleProductIconByType(type)

  /*=========================
    QUICK BUY MODAL
   =========================*/
  const handleQuickBuyModal = () => {
    handleOpenQuickBuyModal(true)
    handleProduct(product)
    handleShopify(shopifyProduct)
    if (color && type !== "merchandise") {
      handleSelectedProductColor(color)
    }
  }

  return (
    <div
      className={`product-box ${notice.length ? "product-box--notice" : ""} ${
        imgOrientation === "vertical" ? "product-box--vertical" : ""
      }`}
      data-notice={notice.length ? notice : ""}
    >
      <Link
        to={`/products/${shopifyProduct.handle}${urlParam || ""}`}
        state={{ selectedProductColor: color }}
        className={`product-box__img ${imgOrientation === "vertical" ? "product-box__img--vertical" : ""} ${
          imgOrientation === "vertical-top" ? "product-box__img--vertical-top" : ""
        }`}
      >
        <span
          className={`product-box--tag product-box--tag-${icon} ${type === "bundles" ? "product-box--tag-sm" : ""}`}
        >
          <span>{type}</span>
          {icon === "tshirt" ? (
            <img loading="lazy" src="/images/heart_hanger.svg" alt="" />
          ) : icon === "tag" ? (
            <img src="/images/tag.svg" alt="" width={19} height={19} className="tag--sm" />
          ) : (
            <i className={`${icon !== "flex" ? "fa-solid" : ""} fa-${icon}`} />
          )}
        </span>

        <ProductBoxImage loading={aboveTheFold} filename={`${imageSrc}`} className={`product-box__img--regular`} />
        <ProductBoxImage loading={aboveTheFold} filename={`${imageSrc}-hover`} className="product-box__img--hover" />

        {isOutOfStock && <OutOfStock box={false} />}
      </Link>
      <div className="product-box__content">
        <h3 className="h4 product-box__title">
          <Link
            to={`/products/${shopifyProduct.handle}${urlParam || ""}`}
            state={{ selectedProductColor: color }}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        </h3>
        <div className="product-box__text" dangerouslySetInnerHTML={{ __html: shopifyProduct.descriptionHtml }} />

        <div className="product-box__buttons">
          <Link
            to={`/products/${shopifyProduct.handle}${urlParam || ""}`}
            state={{ selectedProductColor: color }}
            className="btn btn--md mb--20"
          >
            learn more
          </Link>
          <button onClick={handleQuickBuyModal} className="link link--sm">
            Buy now
          </button>
        </div>
      </div>
    </div>
  )
}
export default ProductBox
